import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useApi } from '../hooks/useApi';
import { useToast } from '../components/Shared/Toast';
import { useTranslation } from '../contexts/LanguageContext';
import ToolGrid from '../components/Dashboard/ToolGrid';
import { ENV_STORAGE_PREFIX } from '../components/Dashboard/ToolCard';

const STATUS_POLL_INTERVAL_MS = 60000;

function loadToolEnvFromStorage(tools) {
  const map = {};
  for (const t of tools || []) {
    const saved = localStorage.getItem(ENV_STORAGE_PREFIX + t.tool_id);
    if (saved && (t.environments || []).includes(saved)) {
      map[t.tool_id] = saved;
    } else {
      map[t.tool_id] = (t.environments || ['prod'])[0] || 'prod';
    }
  }
  return map;
}

export default function DashboardPage() {
  const { api } = useApi();
  const { showToast } = useToast();
  const { t } = useTranslation();

  const [tools, setTools] = useState([]);
  const [statusMap, setStatusMap] = useState({});
  const [toolEnv, setToolEnv] = useState({});
  const [loading, setLoading] = useState(true);
  const pollRef = useRef(null);

  const fetchStatus = useCallback(async () => {
    try {
      const status = await api('auth/tools/status');
      setStatusMap(status || {});
    } catch {
      // best effort — keine Toast-Spam
    }
  }, [api]);

  const loadTools = useCallback(async () => {
    setLoading(true);
    try {
      const [t1, s1] = await Promise.all([
        api('auth/tools'),
        api('auth/tools/status').catch(() => ({})),
      ]);
      setTools(t1 || []);
      setStatusMap(s1 || {});
      setToolEnv(loadToolEnvFromStorage(t1));
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || t('common.error');
      showToast(msg, 'error');
      setTools([]);
    } finally {
      setLoading(false);
    }
  }, [api, showToast, t]);

  useEffect(() => {
    loadTools();
    // Status-Polling
    pollRef.current = setInterval(fetchStatus, STATUS_POLL_INTERVAL_MS);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [loadTools, fetchStatus]);

  const handleEnvChange = (toolId, env) => {
    localStorage.setItem(ENV_STORAGE_PREFIX + toolId, env);
    setToolEnv((prev) => ({ ...prev, [toolId]: env }));
  };

  return (
    <div className="page-content" style={{ paddingTop: 32 }}>
      <div className="tools-header">
        <div>
          <div className="tools-title">{t('dashboard.title')}</div>
          <div className="tools-sub">{t('dashboard.subtitle')}</div>
        </div>
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          onClick={loadTools}
          disabled={loading}
        >
          {t('dashboard.refresh')}
        </button>
      </div>

      <ToolGrid
        tools={tools}
        statusMap={statusMap}
        loading={loading}
        toolEnv={toolEnv}
        onEnvChange={handleEnvChange}
      />
    </div>
  );
}
